import React, { useState, useEffect, useCallback, useRef } from "react";
import { collection, getDocs, doc, deleteDoc, setDoc, updateDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../utils/authContext";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Search, Plus, Edit, Trash2, Database, HelpCircle, AlertTriangle, Link2, Lock, Unlock, Send, Copy } from "lucide-react";
import ModalPortal from "../utils/ModalPortal";
import SchemaFormRenderer from "./SchemaFormRenderer";
import ItemFormDialog from "./ItemFormDialog";
import { saveConfigurationRevision } from './saveConfigurationRevision';
import { initializeFieldBinding, effectiveFieldName } from './dataFieldBinding';

const SCHEMA_DOC_PATH = "globalSettings/documentBuilder";

// Helper to convert display name to camelCase ID
const toCamelCase = (str) => {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special chars
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join("");
};

// Helper to convert display name to snake_case field name
const toSnakeCase = (str) => {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special chars
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.toLowerCase())
    .join("_");
};

/**
 * Schema-based editor component for admin document builder.
 * Uses a schema from Firestore to render a dynamic form for creating/editing items.
 * Falls back to JSON editing if no schema is available.
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.collectionName - Firestore collection name
 * @param {string} config.schemaKey - Key in globalSettings/documentBuilder/schemas (e.g., "stepSchema")
 * @param {string} config.entityName - Singular name (e.g., "Step")
 * @param {string} config.entityNamePlural - Plural name (e.g., "Steps")
 * @param {Object[]} config.columns - Table column definitions
 * @param {React.Component} config.HelpContent - Component to render in help dialog
 * @param {React.Component} config.HeaderControls - Optional component to render additional header controls
 * @param {Function} config.customFilter - Optional function (item) => boolean for additional filtering
 * @param {Object} config.usageData - Optional pre-computed usage data keyed by item id/fieldName
 */
const AdminSchemaBasedEditor = ({ config }) => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [bindingFields, setBindingFields] = useState([]);
  const [schema, setSchema] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("displayName");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [unlockForDeleteOpen, setUnlockForDeleteOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [itemToClone, setItemToClone] = useState(null);
  const [cloneNewId, setCloneNewId] = useState("");
  const [cloneNewName, setCloneNewName] = useState("");
  const [isCloning, setIsCloning] = useState(false);
  const [cloneError, setCloneError] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch schema
      const schemaDoc = await getDoc(doc(db, SCHEMA_DOC_PATH));
      if (schemaDoc.exists()) {
        const schemas = schemaDoc.data().schemas || {};
        setSchema(schemas[config.schemaKey] || null);
      }

      // Fetch items
      const snapshot = await getDocs(collection(db, config.collectionName));
      const data = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setItems(data);
      if (['documentSteps', 'documentSections'].includes(config.collectionName)) {
        const fields = await getDocs(collection(db, 'documentDataFields'));
        setBindingFields(fields.docs.map(snapshot => ({ ...snapshot.data(), id: snapshot.id })));
      }
    } catch (error) {
      console.error(`Error fetching data:`, error);
    }
    setIsLoading(false);
  };

  const handleNew = () => {
    setEditingItem(null);
    setItemDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setItemDialogOpen(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    // If item is locked (published), require unlock first
    if (item.isLocked) {
      setUnlockForDeleteOpen(true);
    } else {
      setDeleteDialogOpen(true);
    }
  };

  const handleUnlockForDeleteConfirm = () => {
    setUnlockForDeleteOpen(false);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    await deleteDoc(doc(db, config.collectionName, itemToDelete.id));
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    fetchData();
  };

  const handleClone = (item) => {
    setItemToClone(item);
    setCloneNewName(`${item[schema?.primaryDisplayField] || item.displayName || item.title || item.name || item.id} Copy`);
    setCloneNewId(`${item.id}Copy`);
    setCloneError("");
    setCloneDialogOpen(true);
  };

  const confirmClone = async () => {
    if (!itemToClone || !cloneNewId.trim() || !cloneNewName.trim()) return;
    
    // Check if ID already exists
    const existingIds = items.map(i => i.id);
    if (existingIds.includes(cloneNewId.trim())) {
      setCloneError("An item with this ID already exists");
      return;
    }
    
    setIsCloning(true);
    setCloneError("");
    try {
      // Clone the item with new ID and name, reset versioning
      const { id, createdAt, updatedAt, updatedBy, versions, version, publishedVersion, isLocked, ...rest } = itemToClone;
      
      const clonedData = {
        ...rest,
        id: cloneNewId.trim(),
        displayName: cloneNewName.trim(),
        name: cloneNewName.trim(),
        ...(schema?.primaryDisplayField ? { [schema.primaryDisplayField]: cloneNewName.trim() } : {}),
        version: 1,
        versions: [],
        publishedVersion: null,
        isLocked: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      };
      
      await saveConfigurationRevision(db, doc(db, config.collectionName, cloneNewId.trim()), null, clonedData, {
        create: true,
        duplicateMessage: 'An item with this ID already exists',
      });
      setCloneDialogOpen(false);
      setItemToClone(null);
      setCloneNewId("");
      setCloneNewName("");
      fetchData();
    } catch (error) {
      console.error(`Error cloning ${config.entityName}:`, error);
      setCloneError(error.message === 'An item with this ID already exists'
        ? error.message : "Failed to clone: " + error.message);
    }
    setIsCloning(false);
  };

  const displayItem = item => ['documentSteps', 'documentSections'].includes(config.collectionName)
    ? initializeFieldBinding(item, bindingFields, config.collectionName === 'documentSteps') : item;

  const filteredItems = items
    .filter(item => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return ['id', 'displayName', ...(config.searchFields || [])]
        .some(key => typeof item[key] === 'string' && item[key].toLowerCase().includes(query));
    })
    .filter(item => {
      // Apply custom filter if provided
      if (config.customFilter) {
        return config.customFilter(item);
      }
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      if (sortBy === "updatedAt") {
        aVal = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0);
        bVal = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0);
      } else if (sortBy === "displayName") {
        aVal = (a.displayName || a.name || a.id || "").toLowerCase();
        bVal = (b.displayName || b.name || b.id || "").toLowerCase();
      } else {
        // Check if column has a sortValue function
        const column = config.columns.find(c => c.key === sortBy);
        if (column?.sortValue) {
          aVal = column.sortValue(displayItem(a));
          bVal = column.sortValue(displayItem(b));
        } else {
          // For other columns, try to get a sortable value
          aVal = displayItem(a)[sortBy];
          bVal = displayItem(b)[sortBy];
        }
        // Handle string comparison
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        // Handle null/undefined
        if (aVal == null) aVal = "";
        if (bVal == null) bVal = "";
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    });
  };

  return (
    <div style={{ marginBottom: '60px' }}>
      {/* No schema warning */}
      {!isLoading && !schema && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Editor unavailable.
        </Alert>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div className="home-header-search-wrapper" style={{ width: 'auto' }}>
            <Tooltip title={`Search ${config.entityNamePlural}`} arrow>
              <Search className="header-search-icon" size={18} />
            </Tooltip>
            <input
              type="text"
              placeholder={`Search ${config.entityNamePlural}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="header-search-input"
            />
          </div>
          {config.HeaderControls && <config.HeaderControls />}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Documentation" arrow>
            <IconButton onClick={() => setHelpDialogOpen(true)} size="small">
              <HelpCircle size={20} />
            </IconButton>
          </Tooltip>
          {!config.disableCreate && (
            <Button
              startIcon={<Plus size={18} />}
              onClick={handleNew}
              variant="contained"
              color="primary"
            >
              Add {config.entityName}
            </Button>
          )}
        </div>
      </div>

      {isLoading && (
        <Box className="table-loading-bar">
          <LinearProgress />
        </Box>
      )}

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-background-alt)' }}>
              {config.columns.map(col => (
                <TableCell 
                  key={col.key} 
                  sx={{ fontWeight: 600 }}
                  align={col.align || 'left'}
                  sortDirection={sortBy === col.key ? sortDirection : false}
                >
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDirection : "asc"}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.headerTooltip ? (
                      <Tooltip title={col.headerTooltip} arrow>
                        <span>{col.label}</span>
                      </Tooltip>
                    ) : (
                      col.label
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell 
                sx={{ fontWeight: 600 }}
                sortDirection={sortBy === "updatedAt" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortBy === "updatedAt"}
                  direction={sortBy === "updatedAt" ? sortDirection : "asc"}
                  onClick={() => handleSort("updatedAt")}
                >
                  Updated
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={config.columns.length + 2} align="center" sx={{ py: 4 }}>
                  {isLoading ? (
                    "Loading..."
                  ) : searchQuery ? (
                    `No ${config.entityNamePlural.toLowerCase()} match your search`
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Database size={48} color="var(--color-text-secondary)" />
                      <span>No {config.entityNamePlural.toLowerCase()} found.{!config.disableCreate && ` Add your first ${config.entityName.toLowerCase()} to get started.`}</span>
                      {!config.disableCreate && (
                        <Button
                          startIcon={<Plus size={16} />}
                          onClick={handleNew}
                          variant="outlined"
                          size="small"
                        >
                          Add First {config.entityName}
                        </Button>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow 
                  key={item.id}
                  sx={{ 
                    '&:hover': { backgroundColor: 'var(--color-background-alt)' },
                    cursor: 'pointer'
                  }}
                  onClick={() => handleEdit(item)}
                >
                  {config.columns.map(col => (
                    <TableCell key={col.key} align={col.align || 'left'}>
                      {col.render ? col.render(displayItem(item)) : (
                        <span className={col.className || ''}>
                          {displayItem(item)[col.key]}
                        </span>
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <span className="patient-details-small">
                      {formatDate(item.updatedAt)}
                    </span>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit" arrow>
                      <IconButton
                        onClick={() => handleEdit(item)}
                        size="small"
                        color="primary"
                      >
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                    {!config.disableCreate && (
                      <Tooltip title="Clone" arrow>
                        <IconButton
                          onClick={() => handleClone(item)}
                          size="small"
                        >
                          <Copy size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!config.disableDelete && (
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          onClick={() => handleDelete(item)}
                          size="small"
                          color="error"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Item Create/Edit Dialog */}
      <ModalPortal isOpen={itemDialogOpen}>
        <ItemFormDialog
          open={itemDialogOpen}
          onClose={() => setItemDialogOpen(false)}
          editingItem={editingItem}
          currentUser={currentUser}
          config={config}
          schema={schema}
          onSave={() => {
            setItemDialogOpen(false);
            fetchData();
          }}
        />
      </ModalPortal>

      {/* Delete Confirmation Dialog */}
      <ModalPortal isOpen={deleteDialogOpen}>
        <DeleteDialog
          open={deleteDialogOpen}
          onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null); }}
          item={itemToDelete}
          config={config}
          onConfirm={confirmDelete}
        />
      </ModalPortal>

      {/* Unlock for Delete Dialog */}
      <ModalPortal isOpen={unlockForDeleteOpen}>
        <Dialog open={unlockForDeleteOpen} onClose={() => { setUnlockForDeleteOpen(false); setItemToDelete(null); }} sx={{ zIndex: 10002 }}>
          <DialogTitle>Unlock Before Deleting?</DialogTitle>
          <DialogContent>
            <Typography>
              This {config.entityName.toLowerCase()} is locked because it has been published. 
              You must unlock it before deleting.
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Deleting a published {config.entityName.toLowerCase()} may affect existing documents that use it.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setUnlockForDeleteOpen(false); setItemToDelete(null); }}>Cancel</Button>
            <Button onClick={handleUnlockForDeleteConfirm} variant="contained" color="warning" startIcon={<Unlock size={16} />}>
              Unlock & Continue
            </Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {/* Clone Dialog */}
      <ModalPortal isOpen={cloneDialogOpen}>
        <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Clone {config.entityName}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Create a copy of "{itemToClone?.[schema?.primaryDisplayField] || itemToClone?.displayName || itemToClone?.title || itemToClone?.name || itemToClone?.id}" with a new ID and name.
              </Typography>
              <TextField
                label="New Name"
                value={cloneNewName}
                onChange={(e) => {
                  setCloneNewName(e.target.value);
                  setCloneNewId(toCamelCase(e.target.value));
                }}
                fullWidth
                size="small"
                autoFocus
              />
              <TextField
                label="New ID"
                value={cloneNewId}
                onChange={(e) => setCloneNewId(e.target.value)}
                fullWidth
                size="small"
                helperText="Must be unique"
              />
              {cloneError && (
                <Alert severity="error">{cloneError}</Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={confirmClone} 
              variant="contained" 
              disabled={isCloning || !cloneNewId.trim() || !cloneNewName.trim()}
              startIcon={<Copy size={16} />}
            >
              {isCloning ? "Cloning..." : "Clone"}
            </Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {/* Help Dialog */}
      <ModalPortal isOpen={helpDialogOpen}>
        <HelpDialog
          open={helpDialogOpen}
          onClose={() => setHelpDialogOpen(false)}
          config={config}
        />
      </ModalPortal>
    </div>
  );
};

// Dialog for creating/editing items - uses schema-driven form
const ItemDialog = ({ open, onClose, editingItem, currentUser, config, schema, onSave }) => {
  const [id, setId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [identityFieldValue, setIdentityFieldValue] = useState("");
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  const [identityFieldManuallyEdited, setIdentityFieldManuallyEdited] = useState(false);
  const [identityFieldUnlocked, setIdentityFieldUnlocked] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  
  // Tabs and versioning state
  const [activeTab, setActiveTab] = useState(0); // 0 = Edit, 1 = Versions
  const [sessionUnlocked, setSessionUnlocked] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  
  // Data field tracking for steps/sections
  const [dataFieldsRegistry, setDataFieldsRegistry] = useState([]);
  const [unpairedFields, setUnpairedFields] = useState([]);
  const [selectedUnpairedField, setSelectedUnpairedField] = useState(null);
  const [dataFieldWarning, setDataFieldWarning] = useState(null);
  
  // Check if this is a step or section collection (needs data field handling)
  const isStepCollection = config.collectionName === "documentSteps";
  const isSectionCollection = config.collectionName === "documentSections";
  const needsDataFieldHandling = isStepCollection || isSectionCollection;
  
  // Version/publish status derived from editingItem (skip if versioning disabled)
  const isLocked = !config.disableVersioning && editingItem?.isLocked && !sessionUnlocked;
  const hasUnpublishedChanges = !config.disableVersioning && editingItem && (
    !editingItem.publishedVersion || editingItem.version !== editingItem.publishedVersion
  );

  // Find the identity field from schema (marked with identityField: true)
  const identityField = schema?.fields?.find(f => f.identityField);
  const hasPriority = schema?.fields?.some(f => f.key === 'priority');
  
  // Check if schema specifies a primary display field (e.g., "title" for sections)
  const primaryDisplayFieldKey = schema?.primaryDisplayField;
  const primaryDisplayField = primaryDisplayFieldKey 
    ? schema?.fields?.find(f => f.key === primaryDisplayFieldKey) 
    : null;

  // Fetch data fields registry for steps/sections
  useEffect(() => {
    const fetchDataFields = async () => {
      if (!needsDataFieldHandling || !open) return;
      try {
        const snapshot = await getDocs(collection(db, "documentDataFields"));
        const fields = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDataFieldsRegistry(fields);
        
        // Find unpaired fields created in the last 3 days
        if (!editingItem) {
          const threeDaysAgo = new Date();
          threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
          
          let unpaired;
          if (isSectionCollection) {
            // For sections: fields with writer but no reader
            unpaired = fields.filter(f => 
              f.usedBySteps?.length > 0 && (!f.usedBySections || f.usedBySections.length === 0)
            );
          } else if (isStepCollection) {
            // For steps: fields with reader but no writer
            unpaired = fields.filter(f => 
              f.usedBySections?.length > 0 && (!f.usedBySteps || f.usedBySteps.length === 0)
            );
          }
          
          if (unpaired) {
            // Filter to last 3 days and sort by createdAt descending
            unpaired = unpaired
              .filter(f => {
                if (!f.createdAt) return false;
                const createdDate = f.createdAt.toDate ? f.createdAt.toDate() : new Date(f.createdAt.seconds * 1000);
                return createdDate >= threeDaysAgo;
              })
              .sort((a, b) => {
                const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt?.seconds * 1000 || 0);
                const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt?.seconds * 1000 || 0);
                return bDate - aDate; // Most recent first
              })
              .slice(0, 3); // Limit to 3
            
            setUnpairedFields(unpaired);
          }
        }
      } catch (error) {
        console.error("Error fetching data fields registry:", error);
      }
    };
    fetchDataFields();
  }, [open, needsDataFieldHandling, isSectionCollection, isStepCollection, editingItem]);

  // Initialize form when dialog opens
  useEffect(() => {
    if (editingItem) {
      setId(editingItem.id || "");
      // If using primaryDisplayField, displayName comes from that field value
      if (primaryDisplayFieldKey) {
        setDisplayName(editingItem[primaryDisplayFieldKey] || "");
      } else {
        setDisplayName(editingItem.displayName || editingItem.name || editingItem.title || "");
      }
      setIdentityFieldValue(identityField ? editingItem[identityField.key] || "" : "");
      setIdManuallyEdited(true); // Existing items have fixed IDs
      setIdentityFieldManuallyEdited(true); // Existing items have fixed identity fields
      // Exclude metadata fields that shouldn't be in formData
      const { 
        id: _id, 
        displayName: _dn, 
        name: _n, 
        createdAt, 
        updatedAt, 
        updatedBy, 
        versions: _versions,  // Don't include versions in formData
        publishedVersion: _pv, 
        isLocked: _locked,
        ...rest 
      } = editingItem;
      setFormData(rest);
    } else {
      setId("");
      setDisplayName("");
      setIdentityFieldValue("");
      setIdManuallyEdited(false);
      setIdentityFieldManuallyEdited(false);
      // Initialize with defaults from schema
      const defaults = {};
      if (schema?.fields) {
        schema.fields.forEach(field => {
          if (field.defaultValue !== undefined) {
            setNestedValue(defaults, field.key, field.defaultValue);
          }
        });
      }
      setFormData(defaults);
    }
    setError("");
    setErrors({});
    setActiveTab(0); // Reset to Edit tab
    setSessionUnlocked(false); // Reset unlock state
    setIdentityFieldUnlocked(false); // Reset identity field unlock state
  }, [editingItem, open, schema, primaryDisplayFieldKey, identityField]);

  // Helper to set nested values
  const setNestedValue = (obj, path, value) => {
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
  };

  // Handle form field changes
  const handleFormChange = useCallback((key, value) => {
    setFormData(prev => {
      const next = { ...prev };
      const parts = key.split('.');
      let current = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  // Handle display name change with auto-ID and auto-identity field generation
  const handleDisplayNameChange = (value) => {
    setDisplayName(value);
    setSelectedUnpairedField(null); // Clear unpaired selection when typing
    setDataFieldWarning(null);
    
    // If using primaryDisplayField, also update formData
    if (primaryDisplayFieldKey) {
      handleFormChange(primaryDisplayFieldKey, value);
    }
    if (!editingItem && !idManuallyEdited) {
      setId(toCamelCase(value));
    }
    if (!editingItem && !identityFieldManuallyEdited && identityField) {
      // Use the format specified in the schema
      if (identityField.autoFormat === 'snake_case') {
        setIdentityFieldValue(toSnakeCase(value));
      } else if (identityField.autoFormat === 'camelCase') {
        setIdentityFieldValue(toCamelCase(value));
      }
    }
    
    // Check data field registry for conflicts
    if (needsDataFieldHandling && !editingItem && value.trim()) {
      const derivedField = toSnakeCase(value);
      const existing = dataFieldsRegistry.find(f => f.id === derivedField);
      
      if (existing) {
        if (isStepCollection && existing.usedBySteps?.length > 0) {
          setDataFieldWarning({
            type: "error",
            message: `Warning: Step "${existing.usedBySteps[0]}" already writes to "${derivedField}". Multiple writers may cause conflicts.`
          });
        } else if (isSectionCollection && (!existing.usedBySteps || existing.usedBySteps.length === 0)) {
          setDataFieldWarning({
            type: "warning", 
            message: `Note: No step currently writes to "${derivedField}". This section may not receive data.`
          });
        } else if (isSectionCollection && existing.usedBySteps?.length > 0) {
          setDataFieldWarning({
            type: "success",
            message: `Found matching data field "${derivedField}" from step "${existing.usedBySteps[0]}".`
          });
        }
      } else if (isSectionCollection) {
        setDataFieldWarning({
          type: "warning",
          message: `Data field "${derivedField}" doesn't exist yet. It will be created, but no step writes to it.`
        });
      }
    }
  };
  
  // Handle selection of unpaired field
  const handleUnpairedFieldSelect = (field) => {
    setSelectedUnpairedField(field);
    setDisplayName(field.displayName);
    setDataFieldWarning(null);
    if (primaryDisplayFieldKey) {
      handleFormChange(primaryDisplayFieldKey, field.displayName);
    }
    if (!idManuallyEdited) {
      setId(toCamelCase(field.displayName));
    }
  };

  // Check if a field should be visible based on showWhen condition
  const isFieldVisible = (showWhen) => {
    if (!showWhen) return true;
    
    const fieldValue = getNestedValue(formData, showWhen.field);
    
    if ("equals" in showWhen) {
      return fieldValue === showWhen.equals;
    }
    if ("notEquals" in showWhen) {
      return fieldValue !== showWhen.notEquals;
    }
    if ("in" in showWhen) {
      return showWhen.in.includes(fieldValue);
    }
    
    return true;
  };

  // Validate required fields
  const validateForm = () => {
    const newErrors = {};
    
    // Skip ID format validation when editing existing items (ID is already set and immutable)
    if (!editingItem) {
      if (!id.trim()) {
        newErrors._id = "ID is required";
      } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(id.trim())) {
        newErrors._id = "ID must start with a letter and contain only letters, numbers, and underscores";
      }
    }
    
    if (!displayName.trim()) {
      const fieldLabel = primaryDisplayField?.label || "Display name";
      newErrors._displayName = `${fieldLabel} is required`;
    }

    if (schema?.fields) {
      schema.fields.forEach(field => {
        // Skip validation for primaryDisplayField since it's handled above
        if (field.key === primaryDisplayFieldKey) return;
        // Skip hidden fields - they are auto-generated
        if (field.hidden) return;
        // Skip fields that are not visible due to showWhen condition
        if (!isFieldVisible(field.showWhen)) return;
        if (field.required) {
          const value = getNestedValue(formData, field.key);
          if (value === undefined || value === null || value === "") {
            newErrors[field.key] = `${field.label} is required`;
          }
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  // Sanitize data for Firestore - remove undefined values, convert Timestamps, ensure clean structure
  const sanitizeForFirestore = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    
    // Handle Firestore Timestamp objects - convert to ISO string
    if (typeof obj.toDate === 'function') {
      return obj.toDate().toISOString();
    }
    
    // Handle Firestore DocumentReference - convert to path string
    if (obj.path && typeof obj.path === 'string' && obj.id) {
      return obj.path;
    }
    
    if (Array.isArray(obj)) {
      return obj
        .map(item => sanitizeForFirestore(item))
        .filter(item => item !== undefined && item !== null);
    }
    
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined) continue; // Skip undefined
      if (value && typeof value === 'object' && value.constructor?.name === 'FieldValue') {
        // Keep Firestore FieldValue as-is (serverTimestamp, etc.)
        result[key] = value;
      } else if (value && typeof value === 'object') {
        const sanitized = sanitizeForFirestore(value);
        if (sanitized !== null && sanitized !== undefined) {
          result[key] = sanitized;
        }
      } else if (value !== null) {
        result[key] = value;
      }
    }
    return result;
  };

  const handleSave = async () => {
    setError("");

    // Validate
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const itemId = editingItem ? editingItem.id : id.trim();
      
      // Determine the data field for steps/sections
      let dataFieldId = null;
      if (needsDataFieldHandling) {
        if (selectedUnpairedField) {
          dataFieldId = selectedUnpairedField.id;
        } else {
          dataFieldId = toSnakeCase(displayName);
        }
      }
      
      // Version handling for steps/sections
      const currentVersion = editingItem?.version || 0;
      const newVersion = currentVersion + 1;
      
      // Create version snapshot for history
      const versionSnapshot = {
        version: newVersion,
        displayName: displayName.trim(),
        savedAt: new Date().toISOString(),
        savedBy: currentUser?.uid || 'admin',
        // Include key fields in snapshot
        ...formData
      };
      
      // Store fields at root level (flat structure)
      const data = {
        ...formData,
        id: itemId,
        displayName: displayName.trim(),
        name: displayName.trim(), // Keep name in sync for backwards compatibility
        version: newVersion,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      };
      
      // Add data field to the data (prioritize form value over derived value)
      if (isStepCollection) {
        data.output = formData.output || dataFieldId;
      } else if (isSectionCollection) {
        data.dataField = formData.dataField || dataFieldId;
      }

      // Include identity field if present
      if (identityField) {
        data[identityField.key] = identityFieldValue.trim();
      }

      if (editingItem) {
        // Append version to history
        const existingVersions = editingItem.versions || [];
        data.versions = [...existingVersions, versionSnapshot];
        
        // Preserve publishedVersion and isLocked
        data.publishedVersion = editingItem.publishedVersion || null;
        data.isLocked = editingItem.isLocked || false;
        
        // Sanitize data before saving to Firestore
        const sanitizedData = sanitizeForFirestore(data);
        
        await updateDoc(doc(db, config.collectionName, editingItem.id), sanitizedData);
        
        // Create data field if it doesn't exist (but don't track usage arrays)
        if (needsDataFieldHandling) {
          const newDataField = isStepCollection ? data.output : data.dataField;
          if (newDataField) {
            await ensureDataFieldExists(newDataField, displayName.trim());
          }
        }
      } else {
        data.createdAt = serverTimestamp();
        data.versions = [versionSnapshot];
        data.publishedVersion = null;
        data.isLocked = false;
        
        // Sanitize data before saving to Firestore
        const sanitizedData = sanitizeForFirestore(data);
        await setDoc(doc(db, config.collectionName, itemId), sanitizedData);
        
        // Create data field if it doesn't exist
        const newDataField = isStepCollection ? data.output : data.dataField;
        if (needsDataFieldHandling && newDataField) {
          await ensureDataFieldExists(newDataField, displayName.trim());
        }
      }
      onSave();
    } catch (error) {
      console.error(`Error saving ${config.entityName}:`, error);
      setError("Failed to save: " + error.message);
    }
    setSaving(false);
  };
  
  // Handle publishing the current version (also saves current form data)
  const handlePublish = async () => {
    if (!editingItem) return;
    
    setPublishing(true);
    setError("");
    try {
      const currentVersion = editingItem.version || 1;
      
      // Determine the data field for steps/sections
      let dataFieldId = null;
      if (needsDataFieldHandling) {
        if (selectedUnpairedField) {
          dataFieldId = selectedUnpairedField.id;
        } else {
          dataFieldId = toSnakeCase(displayName);
        }
      }
      
      // Create version snapshot for history
      const versionSnapshot = {
        version: currentVersion,
        displayName: displayName.trim(),
        savedAt: new Date().toISOString(),
        savedBy: currentUser?.uid || 'admin',
        ...formData
      };
      
      // Build data object with all form data
      const data = {
        ...formData,
        displayName: displayName.trim(),
        name: displayName.trim(),
        version: currentVersion,
        publishedVersion: currentVersion,
        isLocked: true,
        versions: [...(editingItem.versions || []), versionSnapshot],
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      };
      
      // Add data field to the data (prioritize form value over derived value)
      if (isStepCollection) {
        data.output = formData.output || dataFieldId;
      } else if (isSectionCollection) {
        data.dataField = formData.dataField || dataFieldId;
      }
      
      // Include identity field if present
      if (identityField) {
        data[identityField.key] = identityFieldValue.trim();
      }
      
      // Sanitize data before saving to Firestore
      const sanitizedData = sanitizeForFirestore(data);
      await updateDoc(doc(db, config.collectionName, editingItem.id), sanitizedData);
      
      // Create data field if it doesn't exist (but don't track usage arrays)
      if (needsDataFieldHandling) {
        const newDataField = isStepCollection ? data.output : data.dataField;
        if (newDataField) {
          await ensureDataFieldExists(newDataField, displayName.trim());
        }
      }
      
      onSave();
    } catch (error) {
      console.error(`Error publishing ${config.entityName}:`, error);
      setError("Failed to publish: " + error.message);
    }
    setPublishing(false);
  };
  
  // Handle unlock confirmation
  const handleUnlockConfirm = () => {
    setSessionUnlocked(true);
    setUnlockDialogOpen(false);
  };
  
  // Ensure the data field exists in documentDataFields (create if new)
  const ensureDataFieldExists = async (dataFieldId, itemDisplayName) => {
    const fieldRef = doc(db, "documentDataFields", dataFieldId);
    const fieldDoc = await getDoc(fieldRef);
    
    if (!fieldDoc.exists()) {
      // Create new field with default category and showInHistoryFields
      await setDoc(fieldRef, {
        id: dataFieldId,
        displayName: itemDisplayName,
        description: "",
        category: "New",
        showInHistoryFields: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    // If field exists, don't update it - usage is computed on display
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth 
      sx={{ zIndex: 'var(--z-modal-backdrop)' }}
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{editingItem ? `Edit ${config.entityName}` : `Add ${config.entityName}`}</span>
            {editingItem && (
              <Chip 
                label={`v${editingItem.version || 1}`} 
                size="small" 
                variant="outlined"
                sx={{ fontSize: 'var(--text-xs)' }}
              />
            )}
            {isLocked && (
              <Tooltip title="Locked - click to unlock for this session" arrow>
                <IconButton size="small" onClick={() => setUnlockDialogOpen(true)} sx={{ p: 0.5 }}>
                  <Lock size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {hasPriority && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.priority}
                    onChange={(e) => handleFormChange('priority', e.target.checked)}
                    disabled={isLocked}
                  />
                }
                label="Priority Step"
                sx={{ mr: 0 }}
              />
            </Box>
          )}
        </Box>
        {/* Tabs - only show for existing items with versioning enabled */}
        {editingItem && !config.disableVersioning && (
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ minHeight: 36 }}>
            <Tab label="Edit" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="Versions" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>
        )}
      </DialogTitle>
      
      <DialogContent>
        {/* Edit Tab */}
        {activeTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Unpaired fields - show when creating new step/section */}
          {needsDataFieldHandling && !editingItem && unpairedFields.length > 0 && !selectedUnpairedField && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'var(--color-background-alt)', 
              borderRadius: 1,
              border: '1px solid var(--color-border)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Link2 size={16} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Recent Unpaired Data Fields
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', mb: 1.5 }}>
                {isSectionCollection 
                  ? "These fields have a Step that writes to them but no Section to display them:"
                  : "These fields have a Section that reads them but no Step to populate them:"}
              </Typography>
              <List dense sx={{ bgcolor: 'var(--color-background)', borderRadius: 1, p: 0 }}>
                {unpairedFields.map(field => (
                  <ListItem key={field.id} disablePadding>
                    <ListItemButton 
                      onClick={() => handleUnpairedFieldSelect(field)}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemText 
                        primary={field.displayName}
                        secondary={isSectionCollection 
                          ? `From step: ${field.usedBySteps?.join(", ")}`
                          : `Used by: ${field.usedBySections?.join(", ")}`}
                      />
                      <Chip label={field.id} size="small" variant="outlined" />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {/* Identity Fields - Display Name auto-generates ID and identity field */}
          <Box sx={{ 
            p: 2, 
            bgcolor: 'var(--color-background-alt)', 
            borderRadius: 1,
            border: '1px solid var(--color-border)'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              {selectedUnpairedField ? 'Selected Data Field' : 'Identity'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedUnpairedField ? (
                // Show selected unpaired field
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedUnpairedField.displayName}</Typography>
                    <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                      Data field: {selectedUnpairedField.id} • {isSectionCollection 
                        ? `From step: ${selectedUnpairedField.usedBySteps?.join(", ")}`
                        : `Used by: ${selectedUnpairedField.usedBySections?.join(", ")}`}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => { setSelectedUnpairedField(null); setDisplayName(""); }}>
                    Change
                  </Button>
                </Box>
              ) : (
                // Show display name input
                <TextField
                  label={primaryDisplayField?.label || "Display Name"}
                  value={displayName}
                  onChange={(e) => handleDisplayNameChange(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={primaryDisplayField?.helpText || `e.g., History of Present Illness`}
                  helperText={errors._displayName}
                  error={!!errors._displayName}
                  autoFocus
                />
              )}
              
              {/* Data field warning */}
              {dataFieldWarning && !selectedUnpairedField && (
                <Alert severity={dataFieldWarning.type} sx={{ py: 0.5 }}>
                  {dataFieldWarning.message}
                </Alert>
              )}
              
              {/* ID field - hidden for steps/sections and when hideDocumentId is set */}
              {!needsDataFieldHandling && !config.hideDocumentId && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="ID (camelCase)"
                    value={id}
                    onChange={(e) => { setId(e.target.value); setIdManuallyEdited(true); }}
                    fullWidth
                    size="small"
                    placeholder={`e.g., historyOfPresentIllness`}
                    helperText={errors._id || (editingItem ? "Cannot be changed" : "Auto-generated")}
                    error={!!errors._id}
                    disabled={!!editingItem}
                    sx={{ flex: 1 }}
                  />
                  {identityField && (
                    <TextField
                      label={identityField.label}
                      value={identityFieldValue}
                      onChange={(e) => { setIdentityFieldValue(e.target.value); setIdentityFieldManuallyEdited(true); }}
                      fullWidth
                      size="small"
                      placeholder={identityField.autoFormat === 'snake_case' ? 'e.g., history_of_present_illness' : ''}
                      helperText="Auto-generated"
                      sx={{ flex: 1 }}
                    />
                  )}
                </Box>
              )}
              
              {/* Identity field for hideDocumentId mode (e.g., Data Fields) */}
              {config.hideDocumentId && identityField && (
                <Box>
                  {editingItem && config.editableIdentityField && !identityFieldUnlocked && (
                    <Alert 
                      severity="warning" 
                      sx={{ mb: 1 }}
                      action={
                        <Button 
                          color="warning" 
                          size="small" 
                          onClick={() => setIdentityFieldUnlocked(true)}
                        >
                          Unlock
                        </Button>
                      }
                    >
                      Changing the field name will break any Steps or Sections that reference it.
                    </Alert>
                  )}
                  <TextField
                    label={identityField.label || "Field Name"}
                    value={identityFieldValue}
                    onChange={(e) => { setIdentityFieldValue(e.target.value); setIdentityFieldManuallyEdited(true); }}
                    fullWidth
                    size="small"
                    placeholder={identityField.autoFormat === 'snake_case' ? 'e.g., chief_complaint' : ''}
                    helperText={errors._identityField || (editingItem && !identityFieldUnlocked ? "Locked - click Unlock to edit" : "The snake_case identifier used in code")}
                    error={!!errors._identityField}
                    disabled={editingItem && config.editableIdentityField && !identityFieldUnlocked}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Schema-driven form */}
          {schema ? (
            <Box sx={{ mt: 1 }}>
              <SchemaFormRenderer
                schema={schema}
                data={formData}
                onChange={handleFormChange}
                errors={errors}
                excludeFields={[
                  ...(hasPriority ? ['priority'] : []), // Priority shown in header
                  ...(identityField ? [identityField.key] : []),  // Identity field shown in identity section
                  ...(primaryDisplayFieldKey ? [primaryDisplayFieldKey] : []),  // Primary display field shown in identity section
                  ...(schema?.fields?.filter(f => f.hidden).map(f => f.key) || [])  // Hidden fields (e.g., output, dataField)
                ]}
              />
            </Box>
          ) : (
            <Alert severity="info">
              Editor unavailable.
            </Alert>
          )}

          {error && (
            <Alert severity="error">{error}</Alert>
          )}
        </Box>
        )}
        
        {/* Versions Tab */}
        {activeTab === 1 && editingItem && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Version History
            </Typography>
            {editingItem.versions?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[...editingItem.versions].reverse().map((ver, idx) => {
                  const isPublished = editingItem.publishedVersion === ver.version;
                  const savedDate = ver.savedAt ? new Date(ver.savedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  }) : "Unknown date";
                  
                  return (
                    <Box 
                      key={ver.version}
                      sx={{ 
                        p: 1.5, 
                        border: '1px solid var(--color-border)',
                        borderRadius: 1,
                        bgcolor: isPublished ? 'var(--color-success-light)' : 'transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Version {ver.version}
                          </Typography>
                          {isPublished && (
                            <Chip label="Published" size="small" color="success" />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {savedDate}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography color="text.secondary">No version history available.</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {editingItem && hasUnpublishedChanges && !config.disableVersioning && (
          <Button 
            onClick={handlePublish} 
            variant="outlined" 
            color="success"
            disabled={publishing}
            startIcon={<Send size={16} />}
          >
            {publishing ? "Publishing..." : "Publish"}
          </Button>
        )}
        <Button 
          onClick={isLocked ? () => setUnlockDialogOpen(true) : handleSave} 
          variant="contained" 
          disabled={saving}
          startIcon={isLocked ? <Lock size={16} /> : null}
        >
          {saving ? "Saving..." : isLocked ? "Unlock" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Unlock Confirmation Dialog - separate from main dialog to avoid nesting issues */}
    <Dialog open={unlockDialogOpen} onClose={() => setUnlockDialogOpen(false)} sx={{ zIndex: 10002 }}>
      <DialogTitle>Unlock for Editing?</DialogTitle>
      <DialogContent>
        <Typography>
          This {config.entityName.toLowerCase()} is locked because it has been published. 
          Unlocking will allow you to make changes for this session only.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUnlockConfirm} variant="contained" startIcon={<Unlock size={16} />}>
          Unlock
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

// Delete confirmation dialog with dependency checking
const DeleteDialog = ({ open, onClose, item, config, onConfirm }) => {
  const [dependencies, setDependencies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmationText, setConfirmationText] = useState("");
  const [checkError, setCheckError] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retry, setRetry] = useState(0);
  const deleteBusy = useRef(false);
  
  const isStep = config.collectionName === "documentSteps";
  const isSection = config.collectionName === "documentSections";
  const isDataField = config.collectionName === "documentDataFields";
  const requireConfirmation = config.requireDeleteConfirmation;
  
  const itemName = item?.displayName || item?.title || item?.name || item?.id || "";
  const confirmationMatches = confirmationText.trim().toLowerCase() === itemName.toLowerCase();

  // Reset confirmation text when dialog opens/closes
  useEffect(() => {
    if (open) {
      setConfirmationText("");
      setDeleteError(false);
    }
  }, [open, item]);

  useEffect(() => {
    let cancelled = false;
    const checkDependencies = async () => {
      if (!open || !item) return;
      setLoading(true);
      setCheckError(false);
      setDependencies(null);
      
      try {
        if (isStep || isSection) {
          const [stepsSnapshot, sectionsSnapshot, fieldsSnapshot] = await Promise.all([
            getDocs(collection(db, 'documentSteps')),
            getDocs(collection(db, 'documentSections')),
            getDocs(collection(db, 'documentDataFields')),
          ]);
          if (cancelled) return;
          const rows = snapshot => snapshot.docs.map(row => ({ ...row.data(), id: row.id }));
          const steps = rows(stepsSnapshot), sections = rows(sectionsSnapshot), fields = rows(fieldsSnapshot);
          const current = (isStep ? steps : sections).find(row => row.id === item.id);
          if (!current) throw new Error('Configuration no longer exists');
          const field = effectiveFieldName(current, fields, isStep);
          const matches = (isStep ? sections : steps)
            .filter(row => field && effectiveFieldName(row, fields, !isStep) === field)
            .map(row => row.displayName || row.title || row.name || row.id);
          setDependencies(matches.length ? {
            type: isStep ? 'sections' : 'writers', field, items: matches,
          } : null);
        } else if (isDataField) {
          // Usage arrays on the registry are not maintained by the editors.
          // Read current references when the confirmation opens instead.
          const [stepsSnapshot, sectionsSnapshot, fieldsSnapshot] = await Promise.all([
            getDocs(collection(db, 'documentSteps')),
            getDocs(collection(db, 'documentSections')),
            getDocs(collection(db, 'documentDataFields')),
          ]);
          if (cancelled) return;
          const fieldIds = new Set(fieldsSnapshot.docs.map(field => field.id));
          const usesField = (data, reference, name) => data[reference] && fieldIds.has(data[reference])
            ? data[reference] === item.id
            : data[name] === (item.fieldName || item.id);
          const usedBySteps = stepsSnapshot.docs
            .filter(step => usesField({ ...step.data(), output: step.data().output || step.data().dataField }, 'outputFieldRef', 'output'))
            .map(step => step.data().displayName || step.data().name || step.id);
          const usedBySections = sectionsSnapshot.docs
            .filter(section => usesField(section.data(), 'dataFieldRef', 'dataField'))
            .map(section => section.data().title || section.data().displayName || section.data().name || section.id);
          if (usedBySteps.length > 0 || usedBySections.length > 0) {
            setDependencies({
              type: "inUse",
              steps: usedBySteps,
              sections: usedBySections
            });
          } else {
            setDependencies(null);
          }
        } else {
          setDependencies(null);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error checking dependencies:", error);
        setDependencies(null);
        setCheckError(true);
      }
      setLoading(false);
    };
    
    checkDependencies();
    return () => { cancelled = true; };
  }, [open, item, isStep, isSection, isDataField, retry]);

  const handleConfirm = async () => {
    if (deleteBusy.current || loading || checkError || (requireConfirmation && !confirmationMatches)) return;
    deleteBusy.current = true;
    setDeleting(true);
    setDeleteError(false);
    try {
      await onConfirm();
    } catch (error) {
      console.error(`Error deleting ${config.entityName}:`, error);
      setDeleteError(true);
    } finally {
      deleteBusy.current = false;
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !deleteBusy.current && onClose()} sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>Delete {config.entityName}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ py: 2 }}>Checking dependencies...</Box>
        ) : checkError ? (
          <Alert severity="error" action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>}>
            Couldn't check dependencies. Try again.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>
              Are you sure you want to delete "{itemName}"?
            </Typography>
            
            {dependencies?.type === "sections" && (
              <Alert severity="warning" icon={<AlertTriangle size={20} />}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Sections reading from "{dependencies.field}":
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {dependencies.items.map((s, index) => (
                    <li key={index}><Typography variant="body2">{s}</Typography></li>
                  ))}
                </Box>
              </Alert>
            )}
            
            {dependencies?.type === "writers" && (
              <Alert severity="info">
                <Typography variant="body2">
                  Steps writing to "{dependencies.field}": {dependencies.items.join(", ")}
                </Typography>
              </Alert>
            )}
            
            {dependencies?.type === "inUse" && (
              <Alert severity="error" icon={<AlertTriangle size={20} />}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  ⚠️ DANGER: This field is actively in use!
                </Typography>
                {dependencies.steps.length > 0 && (
                  <Typography variant="body2">
                    <strong>Steps writing to this field:</strong> {dependencies.steps.join(", ")}
                  </Typography>
                )}
                {dependencies.sections.length > 0 && (
                  <Typography variant="body2">
                    <strong>Sections displaying this field:</strong> {dependencies.sections.join(", ")}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                  Deleting this field will cause errors in document processing and display!
                </Typography>
              </Alert>
            )}
            
            {requireConfirmation && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This action cannot be undone.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  To confirm, type: <strong>{itemName}</strong>
                </Typography>
                <TextField
                  disabled={deleting}
                  fullWidth
                  size="small"
                  placeholder={`Type "${itemName}" to confirm`}
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  error={confirmationText.length > 0 && !confirmationMatches}
                  helperText={confirmationText.length > 0 && !confirmationMatches ? "Name does not match" : ""}
                  sx={{ backgroundColor: 'white' }}
                />
              </Alert>
            )}
            
            {!requireConfirmation && (
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      {deleteError && <Alert severity="error" sx={{ mx: 3, mb: 1 }}>Couldn't delete. Try again.</Alert>}
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button 
          onClick={handleConfirm}
          color="error" 
          variant="contained"
          disabled={loading || checkError || deleting || (requireConfirmation && !confirmationMatches)}
        >
          {deleting ? 'Deleting...' : dependencies ? 'Delete Anyway' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Help documentation dialog
const HelpDialog = ({ open, onClose, config }) => {
  const HelpContent = config.HelpContent;
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>{config.entityNamePlural} Documentation</DialogTitle>
      <DialogContent>
        {HelpContent ? <HelpContent /> : (
          <Box sx={{ color: 'var(--color-text-secondary)' }}>
            No documentation available for {config.entityNamePlural}.
          </Box>

        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminSchemaBasedEditor;
